import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FileText, Tags, X, Upload, Trash2, CheckCircle, AlertCircle, Loader } from "lucide-react";
import { useDocumentCategories } from "../../contexts/DocumentCategoriesContext";

export interface PendingFileUpload {
    file: File;
    name: string;
    category: string;
}

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

export interface FileUploadProgress {
    status: UploadStatus;
    progress: number; // 0-100
    error?: string;
}

interface MultiFileUploadModalProps {
    files: File[];
    visible: boolean;
    defaultCategory?: string;
    uploadProgress: Record<number, FileUploadProgress>;
    isUploading: boolean;
    onConfirm: (uploads: PendingFileUpload[]) => void;
    onCancel: () => void;
}

export const MultiFileUploadModal: React.FC<MultiFileUploadModalProps> = ({
    files,
    visible,
    defaultCategory,
    uploadProgress,
    isUploading,
    onConfirm,
    onCancel,
}) => {
    const { categories } = useDocumentCategories();
    const categoryOptions = useMemo(
        () => categories.map((c) => ({ value: c.key, label: c.label })),
        [categories]
    );

    const [pendingUploads, setPendingUploads] = useState<PendingFileUpload[]>([]);

    useEffect(() => {
        if (files.length > 0) {
            setPendingUploads(
                files.map((file) => ({
                    file,
                    name: getFileNameWithoutExtension(file.name),
                    category: defaultCategory || "",
                }))
            );
        }
    }, [files, defaultCategory]);

    if (!visible || files.length === 0) return null;

    const getFileNameWithoutExtension = (filename: string): string => {
        const lastDotIndex = filename.lastIndexOf(".");
        return lastDotIndex !== -1 ? filename.slice(0, lastDotIndex) : filename;
    };

    const getFileExtension = (filename: string): string => {
        const lastDotIndex = filename.lastIndexOf(".");
        return lastDotIndex !== -1 ? filename.slice(lastDotIndex) : "";
    };

    const handleNameChange = (index: number, newName: string) => {
        if (isUploading) return;
        setPendingUploads((prev) =>
            prev.map((upload, i) => (i === index ? { ...upload, name: newName } : upload))
        );
    };

    const handleCategoryChange = (index: number, newCategory: string) => {
        if (isUploading) return;
        setPendingUploads((prev) =>
            prev.map((upload, i) => (i === index ? { ...upload, category: newCategory } : upload))
        );
    };

    const handleRemoveFile = (index: number) => {
        if (isUploading) return;
        setPendingUploads((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (isUploading) return;
        const validUploads = pendingUploads.filter(
            (upload) => upload.name.trim() && upload.category
        );
        if (validUploads.length > 0) {
            onConfirm(validUploads);
        }
    };

    const handleCancel = () => {
        if (isUploading) return;
        setPendingUploads([]);
        onCancel();
    };

    const allValid = pendingUploads.every(
        (upload) => upload.name.trim() && upload.category
    );

    const allCompleted = Object.keys(uploadProgress).length === pendingUploads.length &&
        Object.values(uploadProgress).every(p => p.status === 'completed');

    const renderStatusIcon = (index: number) => {
        const progress = uploadProgress[index];
        if (!progress) return null;

        switch (progress.status) {
            case 'uploading':
                return <Loader className="text-primary spin" size={16} />;
            case 'completed':
                return <CheckCircle className="text-success" size={16} />;
            case 'error':
                return (
                    <span title={progress.error}>
                        <AlertCircle className="text-danger" size={16} />
                    </span>
                );
            default:
                return null;
        }
    };

    const renderProgressBar = (index: number) => {
        const progress = uploadProgress[index];
        if (!progress || progress.status === 'pending') return null;

        const progressPercent = progress.progress;
        const barColor = progress.status === 'error' ? 'bg-danger' :
                         progress.status === 'completed' ? 'bg-success' : 'bg-primary';

        return (
            <div className="progress mt-1" style={{ height: "4px" }}>
                <div
                    className={`progress-bar ${barColor}`}
                    role="progressbar"
                    style={{ width: `${progressPercent}%`, transition: "width 0.2s ease" }}
                    aria-valuenow={progressPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                />
            </div>
        );
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin { animation: spin 1s linear infinite; }
            `}</style>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <Upload className="me-2" size={18} />
                            {isUploading
                                ? `Laddar upp...`
                                : `Ladda upp ${pendingUploads.length} ${pendingUploads.length === 1 ? "fil" : "filer"}`
                            }
                        </h5>
                        {!isUploading && (
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleCancel}
                                aria-label="Stäng"
                            />
                        )}
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                            {pendingUploads.length === 0 ? (
                                <p className="text-muted text-center py-4">
                                    Inga filer att ladda upp
                                </p>
                            ) : (
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: "40%" }}>
                                                <FileText className="me-1" size={14} />
                                                Namn
                                            </th>
                                            <th style={{ width: "35%" }}>
                                                <Tags className="me-1" size={14} />
                                                Kategori
                                            </th>
                                            <th style={{ width: "15%" }} className="text-center">
                                                Status
                                            </th>
                                            <th style={{ width: "10%" }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingUploads.map((upload, index) => {
                                            const fileProgress = uploadProgress[index];
                                            const isFileUploading = fileProgress?.status === 'uploading';
                                            const isFileCompleted = fileProgress?.status === 'completed';
                                            const rowDisabled = isUploading;

                                            const missingCategory = !upload.category && !isUploading;

                                            return (
                                                <tr key={index} className={isFileCompleted ? 'table-success' : ''}>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={upload.name}
                                                                onChange={(e) =>
                                                                    handleNameChange(index, e.target.value)
                                                                }
                                                                placeholder="Ange filnamn"
                                                                required
                                                                disabled={rowDisabled}
                                                            />
                                                            <span className="text-muted small text-nowrap">
                                                                {getFileExtension(upload.file.name)}
                                                            </span>
                                                        </div>
                                                        {renderProgressBar(index)}
                                                    </td>
                                                    <td>
                                                        <Select
                                                            options={categoryOptions}
                                                            value={categoryOptions.find(
                                                                (opt) => opt.value === upload.category
                                                            ) || null}
                                                            placeholder="Välj kategori..."
                                                            onChange={(val) => {
                                                                if (val) handleCategoryChange(index, val.value);
                                                            }}
                                                            isDisabled={rowDisabled}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menuPortal: (base) => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                }),
                                                                control: (base) => ({
                                                                    ...base,
                                                                    minHeight: "31px",
                                                                    height: "31px",
                                                                    borderColor: missingCategory ? "#dc3545" : base.borderColor,
                                                                    boxShadow: missingCategory ? "0 0 0 1px #dc3545" : base.boxShadow,
                                                                }),
                                                                valueContainer: (base) => ({
                                                                    ...base,
                                                                    height: "31px",
                                                                    padding: "0 6px",
                                                                }),
                                                                input: (base) => ({
                                                                    ...base,
                                                                    margin: "0px",
                                                                }),
                                                                indicatorsContainer: (base) => ({
                                                                    ...base,
                                                                    height: "31px",
                                                                }),
                                                                placeholder: (base) => ({
                                                                    ...base,
                                                                    color: missingCategory ? "#dc3545" : base.color,
                                                                }),
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        {renderStatusIcon(index)}
                                                        {isFileUploading && fileProgress && (
                                                            <span className="ms-1 small text-muted">
                                                                {Math.round(fileProgress.progress)}%
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemoveFile(index)}
                                                            title="Ta bort"
                                                            disabled={rowDisabled}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="modal-footer">
                            {allCompleted ? (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleCancel}
                                >
                                    <CheckCircle className="me-1" size={14} />
                                    Klar
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCancel}
                                        disabled={isUploading}
                                    >
                                        <X className="me-1" size={14} />
                                        Avbryt
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={!allValid || pendingUploads.length === 0 || isUploading}
                                    >
                                        {isUploading ? (
                                            <Loader className="me-1 spin" size={14} />
                                        ) : (
                                            <Upload className="me-1" size={14} />
                                        )}
                                        {isUploading
                                            ? "Laddar upp..."
                                            : `Ladda upp ${pendingUploads.length > 0 ? `(${pendingUploads.length})` : ""}`
                                        }
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
